import { CurrencyPipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { Produto } from 'src/app/produto/shared/produto.model';
import { EntregaDialogComponent } from '../entrega-dialog/entrega-dialog.component';
import { Entrega } from '../shared/entrega.model';

export enum Tipoitem {
  Venda,
  Compra
}

export interface AcaoItem {
  nome: string;
  metodo: (item: any) => void;
  icone?: string;
}

@Component({
  selector: 'app-itens',
  templateUrl: './itens-cadastro.component.html',
  styleUrls: ['./itens-cadastro.component.css'],
})
export class ItensCadastroComponent implements OnInit {
  @Input() getProdutoPorNomeEEmpresasService: (params: any) => Observable<any>;
  @Input() empresas: any;
  @Input() itens: any[] = [];
  @Input() panelOpenState: boolean = true;
  @Input() permitirItemSemId: boolean = false;
  @Input() tipoItem: Tipoitem = Tipoitem.Venda;
  @Input() BloquearEdicao: boolean = false;
  @Input() permitirSubitens: boolean = false;
  @Input() acoesExtras: AcaoItem[] | null = null;

  @Output() valorTotalBruto = new EventEmitter<any>();
  @Output() descontosAplicadosAosItens = new EventEmitter<any>();
  @Output() valorTotalLiquido = new EventEmitter<any>();
  @Output() novosItens = new EventEmitter<any[]>();

  filteredProdutos: any[] = [];
  filteredSubProdutos: any[] = [];

  columns = [
    { def: 'quantidade', header: 'Quantidade' },
    { def: 'descricao', header: 'Nome', minWidth: '250px' },
    { def: 'valorUnitario', header: 'Valor Unitário' },
    { def: 'descontoUnitario', header: 'Valor Desc. Unitário' },
    { def: 'valorTotalBruto', header: 'Valor Total Bruto' },
    { def: 'valorTotalLiquido', header: 'Valor Total Líquido' },
    { def: 'acaoTable', header: 'Ações' },
  ];

  get columnsToDisplay(): string[] {
    const cols = this.columns.map(c => c.def);
    return this.permitirSubitens ? ['expanderCol', ...cols] : cols;
  }

  itemForm: FormGroup;
  editItemForm: FormGroup;
  subitemForm: FormGroup;
  editSubitemForm: FormGroup;

  editingIndex: number | null = null;
  selectedItemMenuIndex: number | null = null;
  addingSubitemIndex: number | null = null;
  editingSubitemKey: { itemIndex: number; subIndex: number } | null = null;
  changingProduct: boolean = false;
  filteredEditProdutos: any[] = [];

  expandedItems = new Set<number>();
  itensTabela = new MatTableDataSource<any>([]);

  selectedSubitemMenuKey: { itemIndex: number; subIndex: number } | null = null;

  constructor(
    private _formBuilder: FormBuilder,
    private currencyPipe: CurrencyPipe,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.itemForm = this._formBuilder.group({
      quantidade: [1, [Validators.required, Validators.min(1)]],
      item: ['', Validators.required],
      descricao: ['', Validators.required],
      codProd: [''],
      valorUnitario: [0, Validators.required],
      descontoUnitario: [0],
      valorTotalBruto: [0, Validators.required],
      valorTotalLiquido: [0, Validators.required],
      produtoId: [0, Validators.required],
      produtoTipo: [0, Validators.required],
    });

    this.  editItemForm = this._formBuilder.group({
      quantidade: [1, [Validators.required, Validators.min(1)]],
      descricao: ['', Validators.required],
      valorUnitario: [0, Validators.required],
      descontoUnitario: [0],
      valorTotalBruto: [0],
      valorTotalLiquido: [0],
      editProdutoSearch: [''],
    });

    this.subitemForm = this._formBuilder.group({
      item: ['', Validators.required],
      descricao: [''],
      codProd: [''],
      produtoId: [0],
      produtoTipo: [0],
      quantidade: [1, [Validators.required, Validators.min(1)]],
      valorUnitario: [0, Validators.required],
      descontoUnitario: [0],
      valorTotalBruto: [0],
      valorTotalLiquido: [0],
    });

    this.editSubitemForm = this._formBuilder.group({
      descricao: ['', Validators.required],
      quantidade: [1, [Validators.required, Validators.min(1)]],
      valorUnitario: [0, Validators.required],
      descontoUnitario: [0],
      valorTotalBruto: [0],
      valorTotalLiquido: [0],
    });

    this.editItemForm.valueChanges.subscribe(() => {
      const valorUnitario = this.editItemForm.get('valorUnitario')?.value || 0;
      const quantidade = this.editItemForm.get('quantidade')?.value || 0;
      const descontoUnitario = this.editItemForm.get('descontoUnitario')?.value || 0;

      this.editItemForm.get('valorTotalBruto')?.setValue(valorUnitario * quantidade, { emitEvent: false });
      this.editItemForm.get('valorTotalLiquido')?.setValue((valorUnitario - descontoUnitario) * quantidade, { emitEvent: false });
    });

    this.editItemForm.get('editProdutoSearch')?.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const searchText = typeof value === 'string' ? value : '';
        if (searchText?.length >= 3) {
          return this.getProdutoPorNomeEEmpresasService({
            id: searchText,
            empresasIds: this.empresas.map((empresa: any) => empresa.empresaId)
          }).pipe(map(response => response.body || []));
        }
        return of([]);
      })
    ).subscribe(data => {
      this.filteredEditProdutos = data;
    });

    this.subitemForm.valueChanges.subscribe(() => {
      const q = this.subitemForm.get('quantidade')?.value || 0;
      const v = this.subitemForm.get('valorUnitario')?.value || 0;
      const d = this.subitemForm.get('descontoUnitario')?.value || 0;

      this.subitemForm.get('valorTotalBruto')?.setValue(q * v, { emitEvent: false });
      this.subitemForm.get('valorTotalLiquido')?.setValue((v - d) * q, { emitEvent: false });
    });

    this.editSubitemForm.valueChanges.subscribe(() => {
      const q = this.editSubitemForm.get('quantidade')?.value || 0;
      const v = this.editSubitemForm.get('valorUnitario')?.value || 0;
      const d = this.editSubitemForm.get('descontoUnitario')?.value || 0;

      this.editSubitemForm.get('valorTotalBruto')?.setValue(q * v, { emitEvent: false });
      this.editSubitemForm.get('valorTotalLiquido')?.setValue((v - d) * q, { emitEvent: false });
    });

    this.subitemForm.get('item')?.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const searchText = typeof value === 'string' ? value : '';
        if (searchText?.length >= 3) {
          return this.getProdutoPorNomeEEmpresasService({
            id: searchText,
            empresasIds: this.empresas.map((empresa: any) => empresa.empresaId)
          }).pipe(map(response => response.body || []));
        }
        return of([]);
      })
    ).subscribe(data => {
      this.filteredSubProdutos = data;
      if (this.permitirItemSemId && !(data.length > 0)) {
        const inputValue = this.subitemForm.get('item')?.value;
        if (typeof inputValue === 'string' && inputValue) {
          this.subitemForm.get('descricao')?.setValue(inputValue, { emitEvent: false });
        }
      }
    });
    this.atualizarPermissaoEdicao();

    this.onChanges();
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.itens) {
      this.setFormatedItensIntoTable();
      this.calcularValoresGerais();
    }

    if(changes.BloquearEdicao){
      this.atualizarPermissaoEdicao();
    }
  }

  mainRowWhen = (_index: number, row: any): boolean => {
    return !row?.detailRow;
  };

  detailRowWhen = (_index: number, row: any): boolean => {
    return !!row?.detailRow;
  };

  openItemMenu(index: number): void {
    this.selectedItemMenuIndex = index;
  }

  executarAcaoMenu(acao: AcaoItem): void {
    if (this.selectedItemMenuIndex === null) return;
    acao.metodo(this.itens[this.selectedItemMenuIndex]);
  }

  onAdicionarSubitemMenu(): void {
    if (this.selectedItemMenuIndex === null) return;
    this.showAddSubitem(this.selectedItemMenuIndex);
  }

  openSubitemMenu(itemIndex: number, subIndex: number): void {
    this.selectedSubitemMenuKey = { itemIndex, subIndex };
  }

  onFormaEntregaItemMenu(): void {
    if (this.selectedItemMenuIndex === null) return;
    this.abrirFormaEntrega(this.selectedItemMenuIndex);
  }

  onFormaEntregaSubitemMenu(): void {
    if (!this.selectedSubitemMenuKey) return;
    const { itemIndex, subIndex } = this.selectedSubitemMenuKey;
    this.abrirFormaEntrega(itemIndex, subIndex);
  }

  abrirFormaEntrega(itemIndex: number, subIndex?: number): void {
    const target = subIndex !== undefined
      ? this.itens[itemIndex]?.subitens?.[subIndex]
      : this.itens[itemIndex];

    if (!target) return;

    const entregaAtual: Entrega = target.entrega
      ? {
          entregaId: target.entrega.entregaId ?? null,
          localEntrega: target.entrega.localEntrega || '',
          nomeDestinatario: target.entrega.nomeDestinatario || '',
          cpfDestinatario: target.entrega.cpfDestinatario || '',
          setorDestinatario: target.entrega.setorDestinatario || '',
          dataEntrega: target.entrega.dataEntrega || null,
        }
      : {
          entregaId: target.entregaId ?? null,
          localEntrega: '',
          nomeDestinatario: '',
          cpfDestinatario: '',
          setorDestinatario: '',
          dataEntrega: null,
        };

    const dialogRef = this.dialog.open(EntregaDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { entrega: entregaAtual }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) return;

      // Botão "Excluir Entrega" → zera entrega/entregaId do item ou subitem.
      if (result.excluir === true) {
        if (subIndex !== undefined) {
          this.itens[itemIndex].subitens[subIndex] = {
            ...this.itens[itemIndex].subitens[subIndex],
            entrega: null,
            entregaId: null,
          };
        } else {
          this.itens[itemIndex] = {
            ...this.itens[itemIndex],
            entrega: null,
            entregaId: null,
          };
        }

        this.setFormatedItensIntoTable();
        this.emitChanges();
        return;
      }

      const entregaFinal: Entrega = {
        entregaId: result.entregaId ?? null,
        localEntrega: result.localEntrega || '',
        nomeDestinatario: result.nomeDestinatario || '',
        cpfDestinatario: result.cpfDestinatario || '',
        setorDestinatario: result.setorDestinatario || '',
        dataEntrega: result.dataEntrega || null,
      };

      if (subIndex !== undefined) {
        this.itens[itemIndex].subitens[subIndex] = {
          ...this.itens[itemIndex].subitens[subIndex],
          entrega: entregaFinal,
          entregaId: entregaFinal.entregaId ?? null,
        };
      } else {
        this.itens[itemIndex] = {
          ...this.itens[itemIndex],
          entrega: entregaFinal,
          entregaId: entregaFinal.entregaId ?? null,
        };
      }

      this.setFormatedItensIntoTable();
      this.emitChanges();
    });
  }

  temAcoesMenu(): boolean {
    const temSubitem = !!this.permitirSubitens;
    const temExtras = !!this.acoesExtras?.length;
    return temSubitem || temExtras;
  }

  onChanges(): void {
    this.itemForm.valueChanges.subscribe(() => {
      const valorUnitario = this.itemForm.get('valorUnitario')?.value || 0;
      const quantidade = this.itemForm.get('quantidade')?.value || 0;
      const descontoUnitario = this.itemForm.get('descontoUnitario')?.value || 0;

      const valorTotalBruto = valorUnitario * quantidade;
      const valorTotalLiquido = (valorUnitario - descontoUnitario) * quantidade;

      this.itemForm.get('valorTotalBruto')?.setValue(valorTotalBruto, { emitEvent: false });
      this.itemForm.get('valorTotalLiquido')?.setValue(valorTotalLiquido, { emitEvent: false });
    });

    this.itemForm.get('item')?.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (value?.length >= 3) {
          return this.getProdutoPorNomeEEmpresasService({
            id: value,
            empresasIds: this.empresas.map((empresa: any) => empresa.empresaId)
          }).pipe(map(response => response.body || []));
        }
        return of([]);
      })
    ).subscribe(data => {
      this.filteredProdutos = data;
      if (this.permitirItemSemId && !(data.length > 0)) {
        this.itemForm.get('descricao')?.setValue(this.itemForm.get('item')?.value);
      }
    });
  }

  displayItemFn(item: any): string {
    return item ? item.descricao : '';
  }

  displaySubItemFn(item: any): string {
    if (!item) return '';
    return typeof item === 'string' ? item : item.descricao || '';
  }

  onItemSelected(item: Produto): void {
    if (!item) return;

    this.itemForm.patchValue({
      valorUnitario: this.tipoItem === Tipoitem.Venda ? item.vlrComercial : item.valorCompra,
      codProd: item.codProd,
      produtoId: item.produtoId,
      produtoTipo: item.produtoTipo,
      descricao: item.descricao,
      descontoUnitario: 0
    });
  }

  onSubItemSelected(item: Produto): void {
    if (!item) return;

    const preco = this.tipoItem === Tipoitem.Venda ? item.vlrComercial : item.valorCompra;
    const q = this.subitemForm.get('quantidade')?.value || 1;
    const desconto = 0;

    this.subitemForm.patchValue({
      descricao: item.descricao,
      codProd: item.codProd,
      produtoId: item.produtoId,
      produtoTipo: item.produtoTipo,
      valorUnitario: preco,
      descontoUnitario: desconto,
      valorTotalBruto: q * preco,
      valorTotalLiquido: (preco - desconto) * q,
    }, { emitEvent: false });
  }

  addItem(): void {
    if (this.permitirItemSemId) {
      this.itemForm.get('descricao')?.setValue(this.itemForm.get('item')?.value);
    }

    this.itens.push(this.itemForm.value);
    this.setFormatedItensIntoTable();
    this.calcularValoresGerais();
    this.panelOpenState = false;
    this.emitChanges();

    this.itemForm.patchValue({
      quantidade: 1,
      item: '',
      descricao: '',
      codProd: '',
      valorUnitario: 0,
      descontoUnitario: 0,
      valorTotalBruto: 0,
      valorTotalLiquido: 0,
      produtoId: 0,
      produtoTipo: 0
    });
  }

  emitChanges(): void {
    this.novosItens.emit(this.itens);
  }

  setFormatedItensIntoTable(): void {
    const data: any[] = [];

    this.itens?.forEach((item, idx) => {
      const mainRow = {
        _itemIndex: idx,
        detailRow: false,
        codProd: item.codProd,
        descricao: item.descricao,
        valorUnitario: this.currencyPipe.transform(item.valorUnitario, 'BRL', 'symbol'),
        produtoId: item.produtoId,
        produtoTipo: item.produtoTipo,
        quantidade: item.quantidade,
        valorTotalBruto: this.currencyPipe.transform(item.valorTotalBruto, 'BRL', 'symbol'),
        descontoUnitario: this.currencyPipe.transform(item.descontoUnitario, 'BRL', 'symbol'),
        valorTotalLiquido: this.currencyPipe.transform(item.valorTotalLiquido, 'BRL', 'symbol'),
        subitens: item.subitens || []
      };

      const detailRow = {
        _itemIndex: idx,
        detailRow: true,
        element: mainRow
      };

      data.push(mainRow);
      data.push(detailRow);
    });

    this.itensTabela.data = data;
  }

  calcularValoresGerais(): void {
    const valorTotalBrutoTemp = this.itens?.reduce((sum, item) => sum + (item.valorTotalBruto || 0), 0) || 0;
    const descontosAplicadosAosItens = this.itens?.reduce((sum, item) => {
      return sum + (((item.descontoUnitario || 0) * (item.quantidade || 1)));
    }, 0) || 0;

    const valorTotalLiquidoTemp = this.itens?.reduce((sum, item) => sum + (item.valorTotalLiquido || 0), 0) || 0;

    this.valorTotalBruto.emit(valorTotalBrutoTemp);
    this.descontosAplicadosAosItens.emit(descontosAplicadosAosItens);
    this.valorTotalLiquido.emit(valorTotalLiquidoTemp);
  }

  get editQuantidade(): FormControl { return this.editItemForm.get('quantidade') as FormControl; }
  get editDescricao(): FormControl { return this.editItemForm.get('descricao') as FormControl; }
  get editValorUnitario(): FormControl { return this.editItemForm.get('valorUnitario') as FormControl; }
  get editDescontoUnitario(): FormControl { return this.editItemForm.get('descontoUnitario') as FormControl; }
  get editProdutoSearch(): FormControl { return this.editItemForm.get('editProdutoSearch') as FormControl; }
  get editValorTotalBruto(): number { return this.editItemForm?.get('valorTotalBruto')?.value || 0; }
  get editValorTotalLiquido(): number { return this.editItemForm?.get('valorTotalLiquido')?.value || 0; }

  get editSubDescricao(): FormControl { return this.editSubitemForm.get('descricao') as FormControl; }
  get editSubQuantidade(): FormControl { return this.editSubitemForm.get('quantidade') as FormControl; }
  get editSubValorUnitario(): FormControl { return this.editSubitemForm.get('valorUnitario') as FormControl; }
  get editSubDescontoUnitario(): FormControl { return this.editSubitemForm.get('descontoUnitario') as FormControl; }
  get editSubValorTotalBrutoControl(): FormControl { return this.editSubitemForm.get('valorTotalBruto') as FormControl; }
  get editSubValorTotalLiquidoControl(): FormControl { return this.editSubitemForm.get('valorTotalLiquido') as FormControl; }
  get editSubValorTotalBruto(): number { return this.editSubitemForm?.get('valorTotalBruto')?.value || 0; }
  get editSubValorTotalLiquido(): number { return this.editSubitemForm?.get('valorTotalLiquido')?.value || 0; }

  get subItemInput(): FormControl { return this.subitemForm.get('item') as FormControl; }
  get subDescricao(): FormControl { return this.subitemForm.get('descricao') as FormControl; }
  get subQuantidade(): FormControl { return this.subitemForm.get('quantidade') as FormControl; }
  get subValorUnitario(): FormControl { return this.subitemForm.get('valorUnitario') as FormControl; }
  get subDescontoUnitario(): FormControl { return this.subitemForm.get('descontoUnitario') as FormControl; }
  get subValorTotalBrutoControl(): FormControl { return this.subitemForm.get('valorTotalBruto') as FormControl; }
  get subValorTotalLiquidoControl(): FormControl { return this.subitemForm.get('valorTotalLiquido') as FormControl; }
  get subValorTotalBruto(): number { return this.subitemForm?.get('valorTotalBruto')?.value || 0; }
  get subValorTotalLiquido(): number { return this.subitemForm?.get('valorTotalLiquido')?.value || 0; }

  startEdit(index: number): void {
    const item = this.itens[index];

    this.editItemForm.patchValue({
      quantidade: item.quantidade,
      descricao: item.descricao,
      valorUnitario: item.valorUnitario,
      descontoUnitario: item.descontoUnitario,
      valorTotalBruto: item.valorTotalBruto,
      valorTotalLiquido: item.valorTotalLiquido,
      editProdutoSearch: '',
    });

    this.editingIndex = index;
    this.changingProduct = false;
    this.filteredEditProdutos = [];
    this.addingSubitemIndex = null;
    this.editingSubitemKey = null;
  }

  startChangeProduct(): void {
    this.changingProduct = true;
    this.editItemForm.get('editProdutoSearch')?.setValue('');
    this.filteredEditProdutos = [];
  }

  cancelChangeProduct(): void {
    this.changingProduct = false;
    this.editItemForm.get('editProdutoSearch')?.setValue('');
    this.filteredEditProdutos = [];
  }

  onEditProductSelected(produto: Produto): void {
    if (!produto) return;

    this.editItemForm.patchValue({
      descricao: produto.descricao,
    });

    // Armazena temporariamente o produto selecionado para uso no saveEdit
    this._selectedEditProduct = produto;
    this.changingProduct = false;
  }

  displayEditProductFn(item: any): string {
    return item ? item.descricao : '';
  }

  getEditingItemProductName(): string {
    if (this.editingIndex === null) return '';
    const item = this.itens[this.editingIndex];
    const produto = item?.produto || item?.item;
    if (produto && typeof produto === 'object' && produto.descricao) {
      return produto.descricao;
    }
    // Fallback: se não tem objeto produto mas tem produtoId, mostra o codProd
    if (item?.codProd) {
      return item.codProd;
    }
    return '';
  }

  _selectedEditProduct: Produto | null = null;

  saveEdit(): void {
    if (this.editingIndex === null) return;

    if (this.itemTemSubitens(this.editingIndex)) {
      const updateData: any = {
        ...this.itens[this.editingIndex],
        descricao: this.editItemForm.get('descricao')?.value,
      };

      if (this._selectedEditProduct) {
        updateData.produtoId = this._selectedEditProduct.produtoId;
        updateData.produtoTipo = this._selectedEditProduct.produtoTipo;
        updateData.codProd = this._selectedEditProduct.codProd;
        updateData.produto = this._selectedEditProduct;
        updateData.item = this._selectedEditProduct;
      }

      this.itens[this.editingIndex] = updateData;
      this.recalcularItemComSubitens(this.editingIndex);
    } else {
      this.itens[this.editingIndex] = {
        ...this.itens[this.editingIndex],
        quantidade: this.editItemForm.get('quantidade')?.value,
        descricao: this.editItemForm.get('descricao')?.value,
        valorUnitario: this.editItemForm.get('valorUnitario')?.value,
        descontoUnitario: this.editItemForm.get('descontoUnitario')?.value,
        valorTotalBruto: this.editItemForm.get('valorTotalBruto')?.value,
        valorTotalLiquido: this.editItemForm.get('valorTotalLiquido')?.value,
      };

      if (this._selectedEditProduct) {
        this.itens[this.editingIndex].produtoId = this._selectedEditProduct.produtoId;
        this.itens[this.editingIndex].produtoTipo = this._selectedEditProduct.produtoTipo;
        this.itens[this.editingIndex].codProd = this._selectedEditProduct.codProd;
        this.itens[this.editingIndex].produto = this._selectedEditProduct;
        this.itens[this.editingIndex].item = this._selectedEditProduct;
      }
    }

    this._selectedEditProduct = null;
    this.changingProduct = false;
    this.editingIndex = null;
    this.setFormatedItensIntoTable();
    this.calcularValoresGerais();
    this.emitChanges();
  }

  cancelEdit(): void {
    this.editingIndex = null;
    this.changingProduct = false;
    this._selectedEditProduct = null;
    this.filteredEditProdutos = [];
  }

  removeItem(index: number): void {
    if (this.editingIndex === index) this.editingIndex = null;
    else if (this.editingIndex !== null && this.editingIndex > index) this.editingIndex--;

    this.expandedItems.delete(index);
    this.itens.splice(index, 1);
    this.setFormatedItensIntoTable();
    this.calcularValoresGerais();
    this.emitChanges();
  }

  private atualizarPermissaoEdicao(): void {
    if (this.BloquearEdicao) {
      this.itemForm.disable({ emitEvent: false });
    } else {
      this.itemForm.enable({ emitEvent: false });
    }
  }

  itemTemSubitens(index: number): boolean {
    return !!(this.itens[index]?.subitens?.length > 0);
  }

  toggleExpand(index: number): void {
    if (this.expandedItems.has(index)) {
      this.expandedItems.delete(index);

      if (this.addingSubitemIndex === index) {
        this.addingSubitemIndex = null;
      }

      if (this.editingSubitemKey?.itemIndex === index) {
        this.editingSubitemKey = null;
      }
    } else {
      this.expandedItems.add(index);
    }

    this.itensTabela.data = [...this.itensTabela.data];
    this.cdr.detectChanges();
  }

  isExpanded(index: number): boolean {
    return this.expandedItems.has(index);
  }

  showAddSubitem(index: number): void {
    this.addingSubitemIndex = index;
    this.expandedItems.add(index);
    this.editingSubitemKey = null;
    this.filteredSubProdutos = [];

    this.subitemForm.reset({
      item: '',
      descricao: '',
      codProd: '',
      produtoId: 0,
      produtoTipo: 0,
      quantidade: 1,
      valorUnitario: 0,
      descontoUnitario: 0,
      valorTotalBruto: 0,
      valorTotalLiquido: 0
    });

    this.itensTabela.data = [...this.itensTabela.data];
    this.cdr.detectChanges();
  }

  hideAddSubitem(): void {
    if (this.addingSubitemIndex !== null) {
      this.expandedItems.delete(this.addingSubitemIndex);
    }

    this.addingSubitemIndex = null;
    this.itensTabela.data = [...this.itensTabela.data];
    this.cdr.detectChanges();
  }

  addSubitem(itemIndex: number): void {
    const itemValue = this.subitemForm.get('item')?.value;

    const quantidade = Number(this.subitemForm.get('quantidade')?.value) || 0;
    const valorUnitario = Number(this.subitemForm.get('valorUnitario')?.value) || 0;
    const valorDescontoUnitario = Number(this.subitemForm.get('descontoUnitario')?.value) || 0;
    const valorTotalBruto = Number(this.subitemForm.get('valorTotalBruto')?.value) || (quantidade * valorUnitario);
    const valorTotalLiquido = Number(this.subitemForm.get('valorTotalLiquido')?.value) || ((valorUnitario - valorDescontoUnitario) * quantidade);
    const valorTotalDesconto = quantidade * valorDescontoUnitario;

    const subitem = {
      faturaSubitemId: 0,
      descricao: this.subitemForm.get('descricao')?.value ||
        (typeof itemValue === 'string' ? itemValue : itemValue?.descricao || ''),
      codProd: this.subitemForm.get('codProd')?.value || '',
      produtoId: this.subitemForm.get('produtoId')?.value || 0,
      produtoTipo: this.subitemForm.get('produtoTipo')?.value || 0,
      quantidade: quantidade,
      valorUnitario: valorUnitario,
      valorDescontoUnitario: valorDescontoUnitario,
      valorTotalBruto: valorTotalBruto,
      valorTotalDesconto: valorTotalDesconto,
      valorTotalLiquido: valorTotalLiquido,
    };

    if (!this.itens[itemIndex].subitens) {
      this.itens[itemIndex].subitens = [];
    }

    this.itens[itemIndex].subitens.push(subitem);
    this.recalcularItemComSubitens(itemIndex);
    this.addingSubitemIndex = null;
    this.setFormatedItensIntoTable();
    this.calcularValoresGerais();
    this.emitChanges();
  }

  removeSubitem(itemIndex: number, subIndex: number): void {
    this.itens[itemIndex].subitens.splice(subIndex, 1);

    if (this.editingSubitemKey?.itemIndex === itemIndex && this.editingSubitemKey?.subIndex === subIndex) {
      this.editingSubitemKey = null;
    }

    this.recalcularItemComSubitens(itemIndex);
    this.setFormatedItensIntoTable();
    this.calcularValoresGerais();
    this.emitChanges();
  }

  startEditSubitem(itemIndex: number, subIndex: number): void {
    const sub = this.itens[itemIndex].subitens[subIndex];

    this.editSubitemForm.patchValue({
      descricao: sub.descricao,
      quantidade: sub.quantidade,
      valorUnitario: sub.valorUnitario,
      descontoUnitario: sub.valorDescontoUnitario ?? sub.descontoUnitario ?? 0,
      valorTotalBruto: sub.valorTotalBruto ?? (sub.valorUnitario * sub.quantidade),
      valorTotalLiquido: sub.valorTotalLiquido ?? ((sub.valorUnitario - (sub.valorDescontoUnitario ?? sub.descontoUnitario ?? 0)) * sub.quantidade),
    });

    this.editingSubitemKey = { itemIndex, subIndex };
    this.addingSubitemIndex = null;
  }

  saveEditSubitem(): void {
    if (!this.editingSubitemKey) return;

    const { itemIndex, subIndex } = this.editingSubitemKey;

    const quantidade = Number(this.editSubitemForm.get('quantidade')?.value) || 0;
    const valorUnitario = Number(this.editSubitemForm.get('valorUnitario')?.value) || 0;
    const valorDescontoUnitario = Number(this.editSubitemForm.get('descontoUnitario')?.value) || 0;
    const valorTotalBruto = Number(this.editSubitemForm.get('valorTotalBruto')?.value) || (quantidade * valorUnitario);
    const valorTotalLiquido = Number(this.editSubitemForm.get('valorTotalLiquido')?.value) || ((valorUnitario - valorDescontoUnitario) * quantidade);
    const valorTotalDesconto = quantidade * valorDescontoUnitario;

    this.itens[itemIndex].subitens[subIndex] = {
      ...this.itens[itemIndex].subitens[subIndex],
      descricao: this.editSubitemForm.get('descricao')?.value,
      quantidade: quantidade,
      valorUnitario: valorUnitario,
      valorDescontoUnitario: valorDescontoUnitario,
      valorTotalBruto: valorTotalBruto,
      valorTotalDesconto: valorTotalDesconto,
      valorTotalLiquido: valorTotalLiquido,
    };

    this.editingSubitemKey = null;
    this.recalcularItemComSubitens(itemIndex);
    this.setFormatedItensIntoTable();
    this.calcularValoresGerais();
    this.emitChanges();
  }

  cancelEditSubitem(): void {
    this.editingSubitemKey = null;
  }

  isEditingSubitem(itemIndex: number, subIndex: number): boolean {
    return this.editingSubitemKey?.itemIndex === itemIndex && this.editingSubitemKey?.subIndex === subIndex;
  }

  recalcularItemComSubitens(itemIndex: number): void {
    const item = this.itens[itemIndex];

    if (item.subitens && item.subitens.length > 0) {
      const valorTotalBruto = item.subitens.reduce((sum: number, s: any) => sum + (s.valorTotalBruto || 0), 0);
      const valorTotalLiquido = item.subitens.reduce((sum: number, s: any) => sum + (s.valorTotalLiquido || 0), 0);
      const descontoTotal = valorTotalBruto - valorTotalLiquido;

      item.valorTotalBruto = Number(valorTotalBruto.toFixed(2));
      item.valorTotalLiquido = Number(valorTotalLiquido.toFixed(2));

      item.quantidade = 1;
      item.valorUnitario = Number(item.valorTotalBruto.toFixed(2));
      item.descontoUnitario = Number(descontoTotal.toFixed(2));
    }
  }
}
